<template>
  <validation-provider
    :disabled="!validatable"
    :name="fieldName || label"
    :rules="validatable ? rules : ''"
    :vid="vid"
    v-slot="validationContext"
    slim
  >
    <b-form-group
      v-bind="$attrs"
      :state="getValidationState(validatable, validationContext)"
      :label-cols-sm="noLabel ? undefined : labelColsSm"
      :class="controlClass"
      :label-class="labelClass"
    >
      <template slot="label">
        <div>
          {{ noLabel ? undefined : fieldLabel }}
          <base-help-icon :help-description="helpDescription" />
        </div>
      </template>
      <slot>
        <b-input-group
          :class="getValidationClass(validatable, validationContext)"
        >
          <b-form-input
            :value="parsedValue"
            type="text"
            :placeholder="$t('common.date_placeholder')"
            :state="getValidationState(validatable, validationContext)"
            @keydown="checkInputValue"
            @input="handleInputValue"
          />
          <b-input-group-append>
            <b-datepicker
              :min="min"
              right
              start-weekday="1"
              button-only
              @input="handlePickerValue"
              :value="parsedPickerValue"
              v-bind="$t('calendar')"
              :locale="$i18n.locale"
              show-decade-nav
            >
              <template #button-content>
                <base-icon icon="calendar-alt" fixed-width />
              </template>
              <template #nav-next-decade>
                <base-icon icon="arrow-to-right" />
              </template>
              <template #nav-next-month>
                <base-icon icon="chevron-right" />
              </template>
              <template #nav-next-year>
                <base-icon icon="chevron-double-right" />
              </template>
              <template #nav-prev-decade>
                <base-icon icon="arrow-to-left" />
              </template>
              <template #nav-prev-month>
                <base-icon icon="chevron-left" />
              </template>
              <template #nav-prev-year>
                <base-icon icon="chevron-double-left" />
              </template>
              <template #nav-this-month>
                <base-icon icon="circle" solid />
              </template>
            </b-datepicker>
          </b-input-group-append>
        </b-input-group>
        <b-form-invalid-feedback
          v-if="validatable"
          :state="getValidationState(validatable, validationContext)"
        >
          {{ validationContext.errors[0] }}
        </b-form-invalid-feedback>
      </slot>
    </b-form-group>
  </validation-provider>
</template>

<script>
import { baseValidatableMixin, dateTimeService } from "@foys-tech/foys-utils";
import { BaseHelpIcon, BaseIcon } from "./";

/**
##Icons dependencies
Add this font awesome icons to your target project

```js
import {
  faCalendarAlt,
  faArrowToRight,
  faChevronRight,
  faChevronDoubleRight,
  faArrowToLeft,
  faChevronLeft,
  faChevronDoubleLeft,
} from '@fortawesome/pro-light-svg-icons';

import {
  faCircle as fasCircle,
} from '@fortawesome/pro-solid-svg-icons';

library.add(
  //light
  faCalendarAlt,
  faArrowToRight,
  faChevronRight,
  faChevronDoubleRight,
  faArrowToLeft,
  faChevronLeft,
  faChevronDoubleLeft,
  //solid
  fasCircle,
);
```
 */
export default {
  mixins: [baseValidatableMixin, dateTimeService],
  components: { BaseHelpIcon, BaseIcon },
  data: () => ({
    datePickerFormat: "yyyy-MM-dd",
  }),
  props: {
    /**
     * Set minimal possible date to select in calendar.
     */
    min: null,
    /**
     * Set format of date to show in the input field
     */
    dateFormat: {
      type: String,
      default: "dd-MM-yyyy",
    },
    rules: {
      type: Object | String,
      default: function () {
        return {
          required: true,
          regex: /((0[1-9]|[12]\d|3[01])-(0[1-9]|1[0-2])-[12]\d{3})$/,
        };
      },
    },
  },
  methods: {
    checkInputValue($event) {
      if (
        this.value &&
        (($event.key == "-" && this.value.endsWith("-")) ||
          ($event.target.value.length == this.dateFormat.length &&
            $event.key != "Backspace" &&
            $event.key != "Tab"))
      ) {
        $event.preventDefault();
      }
    },
    handleInputValue(val) {
      if (!val) {
        this.$emit("input", null);
        return;
      }

      if (val.length < this.dateFormat.length) {
        if (
          val &&
          val.endsWith("-") &&
          ((val.length == 3 && this.value.length == 4) ||
            (val.length == 6 && this.value.length == 7))
        ) {
          val = val.substring(0, val.length - 1);
        } else if (
          val &&
          !val.endsWith("-") &&
          ((val.length == 2 && this.value.length == 1) ||
            (val.length == 5 && this.value.length == 4))
        ) {
          val = val + "-";
        }
        this.$emit("input", val);
        return;
      }

      // 1. convert string-local-format (that is start of day in local tz) to Date()-local
      let localDate = this.parseDateFormat(val, this.dateFormat);

      // 2. switch Date()-local to Date()-utc
      // e.g. 29-09-2021T00:00:00+03 to 29-09-2021T00:00:00Z
      let utcDate = this.isoDateToUtc(localDate);

      // 3. convert Date()-utc to string-utc-iso
      let result = this.formatDateToIsoString(utcDate);

      this.$emit("input", result);
    },
    handlePickerValue(val) {
      if (val) {
        // 1. convert string-local-iso (that is start of day in local tz) to Date()-local
        let localDate = this.parseDateIso(val);

        // 2. switch Date()-local to Date()-utc
        // e.g. 29-09-2021T00:00:00+03 to 29-09-2021T00:00:00Z
        let utcDate = this.isoDateToUtc(localDate);

        // 3. convert Date()-utc to string-utc-iso
        let result = this.formatDateToIsoString(utcDate);

        this.$emit("input", result);
      } else {
        this.$emit("input", null);
      }
    },
    shortDateFilter(value, dateFormat) {
      if (!value || value.length < dateFormat.length) {
        return value;
      }
      // 4. parse string-utc-iso (that is start of day in utc) to Date()-utc
      let utcDate = this.parseDateIso(value);

      // 5. switch Date()-utc to Date()-local
      // e.g. 29-09-2021T00:00:00Z to 29-09-2021T00:00:00+03
      let localDate = this.isoDateToLocal(utcDate);

      // 6. switch Date()-local to string-local-format
      return localDate ? this.formatDate(localDate, dateFormat) : value;
    },
  },
  computed: {
    parsedValue() {
      return this.shortDateFilter(this.value, this.dateFormat);
    },
    parsedPickerValue() {
      if (!this.value) {
        return "";
      }
      let date = this.parseDateIso(this.value);
      return date
        ? this.shortDateFilter(this.value, this.datePickerFormat)
        : "";
    },
  },
};
</script>